import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/errors/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { OpenAIThreadsService } from '@/hooks/ai-agents/services/openai/threads.service';
import { supabase } from '@/integrations/supabase/client';
import { useQuestCompletionTrigger } from '@/components/onboarding';

interface SimplePageFormProps {
  onPageCreated?: () => void;
}

export const SimplePageForm: React.FC<SimplePageFormProps> = ({
  onPageCreated
}) => {
  const [pageName, setPageName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { executeAIOperation } = useAIOperationWrapper();
  const { triggerPageCreated } = useQuestCompletionTrigger();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pageName.trim()) {
      return;
    }

    setIsCreating(true);
    
    try {
      // Get the current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('You must be logged in to create pages');
      }

      // Create dedicated OpenAI thread for this page
      console.log('[SimplePageForm] Creating dedicated OpenAI thread...');
      const threadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      console.log('[SimplePageForm] Thread created:', threadId);

      // Direct database insertion (same logic as PrivateTextGenerator)
      const { data, error } = await supabase
        .from('workbook_page_content')
        .insert({
          workbook_id: null, // Private page
          title: pageName.trim() || 'Untitled Document',
          content: '', // Empty content for blank page
          initial_request: '',
          user_id: user.id,
          openai_thread_id: threadId, // Dedicated thread for this page
          type: null
        })
        .select()
        .single();

      if (error) {
        console.error('[SimplePageForm] Error creating page:', error);
        throw error;
      }
      
      // Invalidate queries to refresh the page list
      queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      
      toast({
        title: "Document Created!",
        description: "Your blank document has been created successfully.",
      });
      
      // Trigger quest completion for Step 1 (Create Document)
      triggerPageCreated();
      
      // Clear form and notify parent
      setPageName('');
      onPageCreated?.();
      
    } catch (error) {
      console.error('[SimplePageForm] Error in page creation:', error);
      toast({
        title: "Creation Failed",
        description: "Failed to create document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Description */}
      <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
        Create a page where you can generate text content with AI assistance
      </p>
      
      {/* Document Name Input */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Document Name
        </label>
        <Input
          id="title"
          type="text"
          placeholder="e.g., Product Requirements Document"
          value={pageName}
          onChange={(e) => setPageName(e.target.value)}
          disabled={isCreating}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Create Button */}
      <motion.div
        whileHover={{ scale: isCreating ? 1 : 1.02 }}
        whileTap={{ scale: isCreating ? 1 : 0.98 }}
      >
        <Button
          type="submit"
          disabled={isCreating || !pageName.trim()}
          className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-center gap-3">
            {isCreating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Creating Page...</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                <span>Create Document</span>
              </>
            )}
          </div>
        </Button>
      </motion.div>
    </form>
  );
}; 